/*
 * Copyright (c) Vaisala Oyj. All rights reserved.
 */

import * as React from "react";
import "./CalculationCard.scss";
import {
  Paper,
  Icon,
  PageContainer,
  Heading,
  Button,
  ButtonType,
  Flex,
  BodyText,
  InputField,
} from "@vaisala-rockhopper/components";
import { useTranslation } from "react-i18next";
import { VaiIcon } from "@vaisala-rockhopper/design-tokens";
import { CurveData } from "../../../../modules/ChemicalDataController";
import CurveGraph from "../calculators/CurveGraph";
import {
  calculateCurve,
  calculatePoint,
} from "../../../../modules/calculator/chemical-curve/CalculatorFunctions";

interface CalculationCardProps {
  curveInformation: CurveData;
  theme: string;
}

const CalculationCard = ({
  curveInformation,
  theme,
}: CalculationCardProps) => {
  const { t } = useTranslation();

  const [temperature, setTemperature] = React.useState<number>(
    parseFloat(curveInformation.Tref.replace(",", ".")) ?? 0
  );
  const [nDValue, setNDValue] = React.useState<string>("");
  const [nDAccuracy, setNDAccuracy] = React.useState<string>("");
  const [concValue, setConcValue] = React.useState<string>("");
  const [concAccuracy, setConcAccuracy] = React.useState<string>("");

  const [calculatedResults, setCalculatedResults] = React.useState<
    Array<{ label: number; value: number }>
  >([]);
  const [calculatedPointResult, setCalculatedPointResult] = React.useState<{
    label: number;
    value: number;
  }>({ label: 0, value: 0 });
  const [calculatedMinMaxPointResult, setCalculatedMinMaxPointResult] =
    React.useState<Array<number>>([]);

  const resetCalculation = () => {
    setTemperature(parseFloat(curveInformation.Tref.replace(",", ".")));
    setNDValue("");
    setNDAccuracy("");
    setConcValue("");
    setConcAccuracy("");
  };

  React.useEffect(() => {
    if (curveInformation.parameter_matrix) {
      setCalculatedResults(
        calculateCurve(
          curveInformation.parameter_matrix,
          temperature,
          curveInformation.Waterbased
        )
      );
    }
  }, [curveInformation, temperature]);

  //   React.useEffect(() => {
  //     console.log(nDValue, curveInformation.parameter_matrix, temperature)
  //     setCalculatedPointResult(calculatePoint(parseFloat(nDValue), 0, curveInformation.parameter_matrix, parseFloat(temperature)))
  //   }, [nDValue, concValue])

  //   React.useEffect(() => {
  //     if (calculatedResults.length > 0) {
  //         let point = calculatedResults.find((val) => val.label === parseFloat(nDValue));
  //         if (point !== undefined) {
  //             setConcValue(point.value!.toString())
  //         } else {
  //             setConcValue("")
  //         }
  //     }
  //   }, [nDValue])

  //   React.useEffect(() => {
  //     if (calculatedResults.length > 0) {
  //         let point = calculatedResults.find((val) => Math.floor(val.value) === parseFloat(concValue));
  //         if (point !== undefined) {
  //             setNDValue(point.label!.toString())
  //         } else {
  //             setNDValue("")
  //         }
  //     }
  //   }, [concValue])

  const findFartherDecimal = (
    reference: number,
    number1: number,
    number2: number
  ): number =>
    Math.abs(reference - number1) > Math.abs(reference - number2)
      ? number1
      : number2;

  const handleNDValueChange = (value: string, accuracy: string = "0") => {
    setNDValue(value);
    let pointResult = { label: 0, value: 0 };
    let minPointResult = { label: 0, value: 0 };
    let maxPointResult = { label: 0, value: 0 };
    if (nDAccuracy !== "" && accuracy !== "0") {
      minPointResult = calculatePoint(
        parseFloat(value) - parseFloat(accuracy),
        0,
        curveInformation.parameter_matrix,
        temperature,
        curveInformation.Waterbased
      );
      maxPointResult = calculatePoint(
        parseFloat(value) + parseFloat(accuracy),
        0,
        curveInformation.parameter_matrix,
        temperature,
        curveInformation.Waterbased
      );
      setCalculatedMinMaxPointResult([
        minPointResult.value,
        maxPointResult.value,
      ]);
    } else {
      setCalculatedMinMaxPointResult([]);
    }
    pointResult = calculatePoint(
      parseFloat(value),
      0,
      curveInformation.parameter_matrix,
      temperature,
      curveInformation.Waterbased
    );
    setConcValue(pointResult.value.toString());
    if (minPointResult.value !== 0 && maxPointResult.value !== 0) {
      const fartherValue = findFartherDecimal(
        pointResult.value,
        minPointResult.value,
        maxPointResult.value
      );
      const concDiff = Math.abs(pointResult.value - fartherValue);
      setConcAccuracy(concDiff.toString());
    }
    setCalculatedPointResult(pointResult);
  };

  const handleNDAccuracyChange = (value: string) => {
    setNDAccuracy(value);
    if (nDValue !== "" && nDValue !== "0") {
      handleNDValueChange(nDValue, value);
    }
  };

  React.useEffect(() => {
    handleNDValueChange(nDValue, nDAccuracy);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [temperature])

  return (
    <PageContainer className="calculation-card">
      <Paper>
        <Flex
          justifyContent="space-between"
          flexDirection="row"
          className="calculation-card-input-row"
        >
          <Heading level={3}>{t("card.calculation")}</Heading>
          <Button
            buttonType={ButtonType.Link}
            onClick={() => resetCalculation()}
            startIcon={<Icon name={VaiIcon.Refresh} />}
          >
            {t("ui.reset")}
          </Button>
        </Flex>
        <Flex
          justifyContent="space-between"
          flexDirection="row"
          className="calculation-card-input-row calculation-temperature"
        >
          <BodyText>{t("card.temperature")}</BodyText>
          <InputField
            name="temperature"
            value={temperature}
            type={"number"}
            onChange={(e) => {
              setTemperature(parseFloat(e.target.value));
            }}
            placeholder={"0"}
          />
        </Flex>
        <CurveGraph
          curveInformation={curveInformation}
          theme={theme}
          calculatedResults={calculatedResults}
          calculatedPointResult={calculatedPointResult}
          nDAccuracy={nDAccuracy}
          calculatedMinMaxPointResult={calculatedMinMaxPointResult}
          chemicalMin={curveInformation.ConcMin}
          chemicalMax={curveInformation.ConcMax}
        />
        <Flex
          justifyContent="space-between"
          flexDirection="row"
          className="calculation-card-input-row"
        >
          <BodyText>{t("card.ri")}</BodyText>
          <Flex
            justifyContent="space-between"
            flexDirection="row"
            className={"calculation-values-accuracy-row"}
          >
            <InputField
              name="ndvalue"
              value={nDValue}
              type={"number"}
              step={0.01}
              onChange={(e) => {
                handleNDValueChange(e.target.value, nDAccuracy);
              }}
              placeholder={"nD"}
            />
            <span>&#177;</span>
            <InputField
              name="ndaccuracy"
              value={nDAccuracy}
              type={"number"}
              step={0.0001}
              onChange={(e) => {
                handleNDAccuracyChange(e.target.value);
                // setNDAccuracy(e.target.value)
              }}
              placeholder={"accuracy"}
            />
          </Flex>
        </Flex>
        <Flex
          justifyContent="space-between"
          flexDirection="row"
          className="calculation-card-input-row"
        >
          <BodyText>{t("card.conc")}</BodyText>
          <Flex
            justifyContent="space-between"
            flexDirection="row"
            className={"calculation-values-accuracy-row"}
          >
            <InputField
              name="concvalue"
              value={concValue}
              type={"number"}
              disabled={true}
              // onChange={e =>
              //     {
              //         setConcValue(e.target.value)
              //     }
              // }
              placeholder={curveInformation.Unit}
            />
            <span>&#177;</span>
            <InputField
              name="concaccuracy"
              value={concAccuracy}
              type={"number"}
              disabled={true}
              // onChange={e =>
              //     {
              //         setConcAccuracy(e.target.value)
              //     }
              // }
              placeholder={"accuracy"}
            />
          </Flex>
        </Flex>
      </Paper>
    </PageContainer>
  );
};
export default CalculationCard;
